import { useForm, Controller } from 'react-hook-form';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Rating from '@mui/material/Rating';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { formHelperTextClasses } from '@mui/material/FormHelperText';

import { useRouter } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';
import { paths, newSearchParams } from 'src/routes/paths';

import { useFavoriteProduct } from 'src/hooks/use-favorite-product';

import { handleShare } from 'src/utils/share';
import { fCurrency, fShortenNumber } from 'src/utils/format-number';

import { useTranslate } from 'src/locales';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { Form, Field } from 'src/components/hook-form';
import { ColorPicker } from 'src/components/color-utils';
import { NumberInput } from 'src/components/number-input';
import { AccountTitle, AccountStatistics } from 'src/components/account-data';

// ----------------------------------------------------------------------

const BENEFITS = [
  {
    title: 'Protected purchase',
    description: 'Receive the product you expected or we will refund your money',
    icon: 'solar:shield-check-bold',
    color: 'success.main',
  },
  {
    title: 'Free returns',
    description: 'You have 30 days from when you receive it',
    icon: 'solar:clock-circle-bold',
    color: 'grey.600',
  },
];

export function ProductDetailsSummary({
  items,
  product,
  reviewHook,
  onAddToCart,
  disableActions,
  ...other
}) {
  const router = useRouter();

  const { t } = useTranslate();

  const { totalRating, totalReviews } = reviewHook;

  const {
    id,
    cod,
    sku,
    card,
    name,
    price,
    country,
    shipping,
    coverUrl,
    priceSale,
    available,
    transaction,
    numberFormat,
    sizes,
    seller,
    colors,
    newLabel,
    sellerUID,
    saleLabel,
    inventoryType,
    shortDescription,
  } = product;

  const productInCart = items?.filter((item) => item.id === id);

  const { isFavorite, toggleFavorite } = useFavoriteProduct({ id });

  const isMaxQuantity = !(productInCart.length
    ? available > productInCart.reduce((total, item) => total + item.quantity, 0)
    : available);

  const defaultValues = {
    id,
    cod,
    sku,
    card,
    name,
    country,
    shipping,
    coverUrl,
    available,
    sellerUID,
    priceSale,
    transaction,
    numberFormat,
    size: sizes?.[0] ?? '',
    colors: colors?.[0] ?? '',
    quantity: available ? 1 : 0,
  };

  const methods = useForm({ defaultValues, values: defaultValues });

  const { watch, control, setValue, handleSubmit } = methods;

  const values = watch();

  const handleAddCart = () => {
    try {
      onAddToCart?.({
        ...values,
        size: [values.size],
        colors: [values.colors],
        subTotal: values.priceSale * values.quantity,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const onSubmit = handleSubmit(async (data) => {
    try {
      if (!productInCart.length) {
        onAddToCart?.({
          ...data,
          size: [data.size],
          colors: [data.colors],
          subTotal: data.priceSale * data.quantity,
        });
      }
      router.push(`${paths.checkout}${newSearchParams({ country })}`);
    } catch (error) {
      console.error(error);
    }
  });

  const renderPrice = () => (
    <Box sx={{ typography: 'h5' }}>
      {saleLabel?.enabled && (
        <Box
          component="span"
          sx={{ color: 'text.disabled', textDecoration: 'line-through', mr: 0.5 }}
        >
          {fCurrency(price, numberFormat)}
        </Box>
      )}

      {fCurrency(priceSale, numberFormat)}
    </Box>
  );

  const renderShare = () => (
    <Stack
      spacing={3}
      direction="row"
      justifyContent="space-around"
      sx={{ flexWrap: 'wrap', color: 'text.secondary' }}
    >
      <Link color="inherit" variant="subtitle2">
        <Iconify icon="mingcute:add-line" width={16} />
        {t('Compare')}
      </Link>

      <Link color="inherit" variant="subtitle2" onClick={toggleFavorite}>
        <Iconify
          icon="solar:heart-bold"
          width={16}
          sx={{ color: isFavorite ? 'error.main' : 'text.secondary' }}
        />
        {t(isFavorite ? 'Remove from favorites' : 'Add to favorites')}
      </Link>

      <Link
        color="inherit"
        variant="subtitle2"
        onClick={() => handleShare(`${paths.product.details(id)}${newSearchParams({ country })}`)}
      >
        <Iconify icon="solar:share-bold" width={16} />
        {t('Share')}
      </Link>
    </Stack>
  );

  const renderColorOptions = () => (
    <Stack direction="row">
      <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
        {t('Color')}
      </Typography>

      <Controller
        name="colors"
        control={control}
        render={({ field }) => (
          <ColorPicker
            limit={6}
            options={colors}
            value={field.value}
            onChange={(color) => field.onChange(color)}
          />
        )}
      />
    </Stack>
  );

  const renderSizeOptions = () => (
    <Stack direction="row">
      <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
        {t('Size')}
      </Typography>

      <Field.Select
        name="size"
        size="small"
        helperText={<Link color="textPrimary">{t('Size chart')}</Link>}
        sx={{
          maxWidth: 88,
          [`& .${formHelperTextClasses.root}`]: { mx: 0, mt: 1, textAlign: 'right' },
        }}
      >
        {sizes.map((size) => (
          <MenuItem key={size} value={size}>
            {size}
          </MenuItem>
        ))}
      </Field.Select>
    </Stack>
  );

  const renderQuantity = () => (
    <Stack direction="row">
      <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
        {t('Quantity')}
      </Typography>

      <Stack spacing={1}>
        <NumberInput
          hideDivider
          value={values.quantity}
          onChange={(event, quantity) => setValue('quantity', quantity)}
          max={available}
          sx={{ maxWidth: 112 }}
        />

        <Typography variant="caption" component="div" sx={{ textAlign: 'right' }}>
          {t('Available')}: {available}
        </Typography>
      </Stack>
    </Stack>
  );

  const renderActions = () => (
    <Stack direction="row" spacing={2}>
      <Button
        fullWidth
        size="large"
        color="success"
        variant="contained"
        disabled={isMaxQuantity || disableActions}
        startIcon={<Iconify icon="solar:cart-plus-bold" width={24} />}
        onClick={handleAddCart}
        sx={{ whiteSpace: 'nowrap' }}
      >
        {t('Add')}
      </Button>

      <Button
        fullWidth
        size="large"
        type="submit"
        variant="contained"
        disabled={isMaxQuantity || disableActions}
      >
        {t('Buy Now')}
      </Button>
    </Stack>
  );

  const renderSubDescription = () => (
    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
      {shortDescription}
    </Typography>
  );

  const renderRating = () => (
    <Stack direction="row" alignItems="center" sx={{ color: 'text.disabled', typography: 'body2' }}>
      <Rating size="small" value={totalRating} precision={0.1} readOnly sx={{ mr: 1 }} />
      {`(${fShortenNumber(totalReviews)} ${t(totalReviews === 1 ? 'review' : 'reviews')})`}
    </Stack>
  );

  const renderLabels = () =>
    (shipping || newLabel?.enabled || saleLabel?.enabled) && (
      <Stack direction="row" alignItems="center" spacing={1}>
        {shipping && (
          <Label color="success">
            <Iconify icon="carbon:delivery-parcel" sx={{ mr: 1 }} />
            {t('Free shipping')}
          </Label>
        )}
        {newLabel?.enabled && <Label color="info">{newLabel.content}</Label>}
        {saleLabel?.enabled && <Label color="error">{saleLabel.content}</Label>}
      </Stack>
    );

  const renderInventoryType = () => (
    <Box
      component="span"
      sx={{
        typography: 'overline',
        color:
          (inventoryType === 'out of stock' && 'error.main') ||
          (inventoryType === 'low stock' && 'warning.main') ||
          'success.main',
      }}
    >
      {t(inventoryType)}
    </Box>
  );

  const renderOwner = () => (
    <Card>
      <Box
        sx={{
          py: 2,
          px: 1.5,
          display: 'flex',
          flexDirection: 'row',
          gap: { xs: 1, md: 1.5 },
          justifyContent: 'space-between',
        }}
      >
        <Avatar
          src={seller?.photoURL}
          sx={{ width: { xs: 30, md: 50 }, height: { xs: 30, md: 50 } }}
        />

        <Stack alignItems="start" sx={{ width: 1 }}>
          <AccountTitle
            user={seller}
            slotProps={{
              iconify: { width: 14 },
              title: { sx: { typography: { xs: 'h6', md: 'subtitle1' } } },
            }}
          />

          <Typography variant="caption" sx={{ color: 'text.secondary', width: 1 }}>
            {seller?.about}
          </Typography>

          <AccountStatistics
            totalSales={seller.totalSales}
            totalRating={seller.totalRating}
            totalFollowers={seller.totalFollowers}
          />
        </Stack>
      </Box>

      <Divider sx={{ borderStyle: 'dashed' }} />

      <Stack
        spacing={3}
        direction="row"
        justifyContent="space-around"
        sx={{ py: 2, px: { xs: 2, md: 0 }, flexWrap: 'wrap', color: 'text.secondary' }}
      >
        <Link
          color="inherit"
          variant="subtitle2"
          component={RouterLink}
          href={`${paths.shop}${newSearchParams({ country, sellerUID: seller.id })}`}
        >
          <Iconify icon="solar:shop-linear" width={16} />
          {t('Visit store')}
        </Link>

        <Link
          color="inherit"
          target="_blank"
          rel="noreferrer"
          variant="subtitle2"
          href={`https://wa.me/${seller?.phoneNumber}`}
        >
          <Iconify icon="ic:round-whatsapp" width={17} sx={{ color: 'success.main' }} />
          {t('Whatsapp')}
        </Link>

        <Link
          color="inherit"
          variant="subtitle2"
          onClick={() =>
            handleShare(`${paths.shop}${newSearchParams({ country, sellerUID: seller.id })}`)
          }
        >
          <Iconify icon="solar:share-bold" width={16} />
          {t('Share')}
        </Link>
      </Stack>
    </Card>
  );

  const renderBenefits = () => (
    <Stack spacing={2}>
      {BENEFITS.map((item) => (
        <Stack key={item.title} direction="row" alignItems="center" spacing={2}>
          <Iconify icon={item.icon} width={24} sx={{ color: item.color }} />

          <Stack>
            <Typography variant="body2">{t(item.title)}</Typography>

            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t(item.description)}.
            </Typography>
          </Stack>
        </Stack>
      ))}
    </Stack>
  );

  return (
    <Form methods={methods} onSubmit={onSubmit}>
      <Stack spacing={3} {...other}>
        <Stack spacing={2} alignItems="flex-start">
          {renderLabels()}
          {renderInventoryType()}

          <Typography variant="h5">{name}</Typography>

          {renderRating()}
          {renderPrice()}
          {renderSubDescription()}
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        {!!colors.length && renderColorOptions()}
        {!!sizes.length && renderSizeOptions()}
        {renderQuantity()}

        <Divider sx={{ borderStyle: 'dashed' }} />

        {renderActions()}
        {renderShare()}

        <Divider sx={{ borderStyle: 'dashed' }} />

        {renderBenefits()}

        <Divider sx={{ borderStyle: 'dashed' }} />

        {renderOwner()}
      </Stack>
    </Form>
  );
}
